// frontend/src/components/wizard/StepRelationships.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { useWizardStore } from '../../store/useWizardStore';
import { useLanguageStore } from '../../store/useLanguageStore';
import { apiClient } from '../../api/client';
import { JoinRulePicker } from './JoinRulePicker';


/**
 * 🆕 StepRelationships (Genérico)
 *
 * Define las relaciones (JOINs/FKs) entre tablas.
 * Reutilizable para ORIGEN y DESTINO.
 *
 * Props:
 *   - mode: 'source' | 'target'
 *   - tables: array de nombres de tablas
 *   - onSave: callback al continuar
 *
 * Uso:
 *   <StepRelationships mode="source" tables={sourceSelectedTables} onSave={handleSourceRelationships} />
 *   <StepRelationships mode="target" tables={targetSelectedTables} onSave={handleTargetRelationships} />
 */

export default function StepRelationships({ mode = 'source' }) {
  const {
    sourceSelectedTables,
    targetSelectedTables,
    sourceColumns,
    targetColumns,
    sourceRelationships = [],
    targetRelationships = [],
    setSourceRelationships,
    setTargetRelationships,
    prevStep,
    nextStep,
  } = useWizardStore();

  const { t } = useLanguageStore();

  // Determinar qué datos usar según el mode
  const tables = mode === 'source' ? sourceSelectedTables : targetSelectedTables;
  const columns = mode === 'source' ? sourceColumns : targetColumns;
  const relationships = mode === 'source' ? sourceRelationships : targetRelationships;
  const setRelationships =
    mode === 'source' ? setSourceRelationships : setTargetRelationships;

  const [relationshipsLocal, setRelationshipsLocal] = useState(relationships);
  const [newRelation, setNewRelation] = useState({
    tableFrom: tables[0] || '',
    // tableTo: tables[1] || '',
    tableTo: '',
    columnFrom: '',
    columnTo: '',
    // 🔧 Target mode usa 'FK' (no es un JOIN de BD, es una relación padre→hijo)
    joinType: mode === 'target' ? 'FK' : 'INNER',
    // joinType: 'INNER',
    rulesFrom: [],   // 🆕 reglas sobre la clave de la tabla primaria
    rulesTo: [],   // 🆕 reglas sobre la clave de la tabla secundaria
    anchorSource: '',   // col del origen que identifica al padre (ej: "country.idCountry")
    bridgeColumn: '',   // col en tabla padre destino donde se guarda el anchor temporalmente
  });
  const [validationError, setValidationError] = useState(null);
  const [cardinalityWarning, setCardinalityWarning] = useState(null); // {parent, table, col}
  const [pendingRelation,    setPendingRelation]    = useState(null);

  // Sincronizar con el store
  useEffect(() => {
    setRelationships(relationshipsLocal);
  }, [relationshipsLocal, setRelationships]);

  const modeLabel = mode === 'source' ? 'Origen' : 'Destino';
  const modeKey = mode === 'source' ? 'source' : 'target';

  const [cachedColumns, setCachedColumns] = useState({});
  const [cachedSourceCols, setCachedSourceCols] = useState({});

  useEffect(() => {
    // Fetch columnas para cada tabla
    const fetchColumnsForTables = async () => {
      const newCached = {};
      for (const table of tables) {
        if (!cachedColumns[table]) {
          try {
            const sourceOrTarget = mode === 'source'
              ? useWizardStore.getState().source
              : useWizardStore.getState().target;

            const res = await apiClient.post('/onboarding/columns', {
              type: sourceOrTarget.type,
              config: { ...sourceOrTarget.config, selectedTable: table },
            });
            // newCached[table] = (res.data.columns || []).map(col =>
            //   typeof col === 'object' ? col.name : col
            // );
            newCached[table] = res.data.columns || [];  // Guardar OBJETOS completos
          } catch (err) {
            console.error(`Error fetching columns for ${table}:`, err);
            newCached[table] = [];
          }
        }
      }
      setCachedColumns(prev => ({ ...prev, ...newCached }));
    };

    if (tables.length > 0) {
      fetchColumnsForTables();
    }
  }, [tables, mode]);

    // Fetch columnas de tablas ORIGEN — solo necesario en modo target
  // para poblar el dropdown de "ID original del padre"
  useEffect(() => {
    if (mode !== 'target') return;
    const srcStore     = useWizardStore.getState().source;
    const srcTables    = useWizardStore.getState().sourceSelectedTables || [];
    if (!srcStore?.config || !srcTables.length) return;

    const fetchSrcCols = async () => {
      const newCache = { ...cachedSourceCols };
      for (const tbl of srcTables) {
        if (newCache[tbl]) continue;
        try {
          const res = await apiClient.post('/onboarding/columns', {
            type:   srcStore.type,
            config: { ...srcStore.config, selectedTable: tbl },
          });
          if (res.data.success && res.data.columns) {
            newCache[tbl] = res.data.columns;
          }
        } catch (e) {
          console.warn(`[StepRelationships] No se pudieron obtener cols de ${tbl}:`, e);
        }
      }
      setCachedSourceCols(newCache);
    };

    fetchSrcCols();
  }, [mode]);

  const addRelationship = () => {
    setValidationError(null);

    // 🔧 NONE no añade entradas — el usuario solo pulsa Continuar
    if (mode === 'target' && newRelation.joinType === 'NONE') return;

    // Validaciones
    if (!newRelation.tableFrom || !newRelation.tableTo) {
      setValidationError(
        t('wizard.relationships.error_select_tables') || 'Selecciona ambas tablas'
      );
      return;
    }
    if (!newRelation.columnFrom || !newRelation.columnTo) {
      setValidationError(
        t('wizard.relationships.error_select_columns') || 'Selecciona ambas columnas'
      );
      return;
    }
    if (newRelation.tableFrom === newRelation.tableTo) {
      setValidationError(
        t('wizard.relationships.error_same_table') ||
        'No puedes relacionar una tabla consigo misma'
      );
      return;
    }

    // Verificar si ya existe
    const exists = relationshipsLocal.some(
      (r) =>
        (r.tableFrom === newRelation.tableFrom && r.tableTo === newRelation.tableTo) ||
        (r.tableFrom === newRelation.tableTo && r.tableTo === newRelation.tableFrom)
    );
    if (exists) {
      setValidationError(
        t('wizard.relationships.error_already_exists') || 'Esta relación ya existe'
      );
      return;
    }

    // ── Detección cardinalidad 1:N (solo source, no target FK) ───────
    if (mode === 'source') {
      const colToMeta = cachedColumns[newRelation.tableTo]
        ?.find(c => c.name === newRelation.columnTo);
      const isUnique  = colToMeta?.is_pk || colToMeta?.is_unique || colToMeta?.is_fk === false;

      if (!isUnique) {
        // Columna secundaria no es PK/UNIQUE → posible 1:N → advertir
        setPendingRelation({ ...newRelation });
        setCardinalityWarning({
          parent: newRelation.tableFrom,
          table:  newRelation.tableTo,
          col:    newRelation.columnTo,
        });
        return;  // No agregar aún — esperar confirmación del usuario
      }
    }

    // Agregar directamente si pasa la validación de cardinalidad
    _commitRelation(newRelation);
  };

  // Helper: confirmar y guardar la relación
  const _commitRelation = (rel) => {
    setRelationshipsLocal(prev => [...prev, rel]);
    setCardinalityWarning(null);
    setPendingRelation(null);
    setNewRelation({
      tableFrom:    tables[0] || '',
      tableTo:      '',
      columnFrom:   '',
      columnTo:     '',
      joinType:     mode === 'target' ? 'FK' : 'INNER',
      rulesFrom:    [],
      rulesTo:      [],
      anchorSource: '',
      bridgeColumn: '',
    });
  };

  const removeRelationship = (idx) => {
    setRelationshipsLocal(relationshipsLocal.filter((_, i) => i !== idx));
  };

  const handleContinue = () => {
    setValidationError(null);

    // 🔧 NONE en target = tablas independientes → no requiere relaciones → pasar directo
    const isNoneMode = mode === 'target' && newRelation.joinType === 'NONE';

    if (!isNoneMode) {
      // Solo validar relaciones si NO es modo "Sin relación"
      const relatedTables = new Set();
      relationshipsLocal.forEach((r) => {
        relatedTables.add(r.tableFrom);
        relatedTables.add(r.tableTo);
      });

      const unrelatedTables = tables.filter((t) => !relatedTables.has(t));
      if (unrelatedTables.length > 0) {
        setValidationError(
          t('wizard.relationships.error_unrelated_tables') ||
          `Las siguientes tablas no tienen relaciones: ${unrelatedTables.join(', ')}`
        );
        return;
      }

      if (relationshipsLocal.length === 0 && tables.length > 1) {
        setValidationError(
          t('wizard.relationships.error_no_relationships') ||
          'Debes definir al menos una relación entre las tablas'
        );
        return;
      }
    }

    nextStep();
  };
  // console.log('🔍 relationshipsLocal:', relationshipsLocal);
  // console.log('🔍 relationshipsLocal.length:', relationshipsLocal.length);
  // console.log('🔍 DEBUG - tables:', tables);
  // console.log('🔍 sourceSelectedTables:', sourceSelectedTables);
  // console.log('🔍 targetSelectedTables:', targetSelectedTables);
  // Obtener columnas disponibles para una tabla
  const getColumnsForTable = (tableName) => {
    // Hacer fetch de columnas por tabla
    if (!tableName) return [];

    // Si ya tiene columnas cacheadas, usarlas
    // if (cachedColumns[tableName]) {
    //   return cachedColumns[tableName];
    // }
    if (cachedColumns[tableName]) {
      // Retornar OBJETOS completos, no solo nombres
      return cachedColumns[tableName];
    }
    return [];
  };

  // // Obtener JOINs soportados del engine
  // const sourceOrTarget = mode === 'source'
  //   ? useWizardStore.getState().source
  //   : useWizardStore.getState().target;

  // // const supportedJoins = sourceOrTarget?.config?.supported_joins || ['INNER', 'LEFT', 'RIGHT', 'FULL'];
  // const supportedJoins = sourceOrTarget?.supported_joins || ['INNER', 'LEFT', 'RIGHT', 'FULL'];

  // Obtener JOINs soportados del engine
  const sourceOrTarget = mode === 'source'
    ? useWizardStore.getState().source
    : useWizardStore.getState().target;

  // 🆕 Obtener supported_joins: primero del store, luego del API si falta
  const [supportedJoins, setSupportedJoins] = useState(['INNER', 'LEFT', 'RIGHT', 'FULL']);

  useEffect(() => {
    const fetchSupportedJoins = async () => {
      // Opción 1: Si ya está en el store (creación nueva)
      if (sourceOrTarget?.supported_joins && sourceOrTarget.supported_joins.length > 0) {
        setSupportedJoins(sourceOrTarget.supported_joins);
        return;
      }

      // Opción 2: Si viene de edición, obtener del API
      if (sourceOrTarget?.type) {
        try {
          // const res = await apiClient.get(`/onboarding/engines/${sourceOrTarget.type}`);
          const res = await apiClient.get(`/catalogs/engines/${sourceOrTarget.type}`);  // ✅ CORRECTO

          // console.log('📍 ENGINE RESPONSE:', sourceOrTarget.type, res.data);  // 🆕 DEBUG

          const joins = res.data?.config?.supported_joins || ['INNER', 'LEFT', 'RIGHT', 'FULL'];
          // console.log('📍 SUPPORTED JOINS:', joins);  // 🆕 DEBUG

          setSupportedJoins(joins);
        } catch (e) {
          console.warn(`Error obteniendo supported_joins para ${sourceOrTarget.type}:`, e);
          setSupportedJoins(['INNER', 'LEFT', 'RIGHT', 'FULL']);
        }
      }
    };

    fetchSupportedJoins();
  }, [sourceOrTarget?.type, sourceOrTarget?.supported_joins]);


  // Validar que TODAS las tablas tengan al menos una relación
  const allTablesHaveRelation = tables.every(table =>
    relationshipsLocal.some(r => r.tableFrom === table || r.tableTo === table)
  );

  return (
    <div className="max-w-4xl mx-auto p-8 bg-white rounded-xl shadow-lg">
      {/* Encabezado */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          {mode === 'source'
            ? t('wizard.step_source_relationships.title') ||
            'Relaciones Entre Tablas Origen'
            : t('wizard.step_target_relationships.title') ||
            'Relaciones Entre Tablas Destino'}
        </h2>
        <p className="text-slate-600">
          {mode === 'source'
            ? t('wizard.step_source_relationships.description') ||
            'Define los JOINs entre tus tablas para garantizar consistencia de datos.'
            : t('wizard.step_target_relationships.description') ||
            'Define los JOINs en el destino para que coincidan con el origen.'}
        </p>
      </div>

      {/* Resumen de tablas seleccionadas */}
      <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800 text-sm">
          <span className="font-semibold">
            📋 {mode === 'source'
              ? t('wizard.relationships.tables_source_selected') || 'Tablas Origen Seleccionadas:'
              : t('wizard.relationships.tables_target_selected') || 'Tablas Destino Seleccionadas:'
            }
          </span>
          <br />
          {tables.join(' • ')}
        </p>
      </div>

      {/* 🆕 GUÍA INTUITIVA */}
      {/* {tables.length > 1 && (
        <div className="mb-6 p-4 bg-indigo-50 border-l-4 border-indigo-600 rounded-r-lg">
          <p className="text-indigo-900 text-sm font-semibold">
            {t('wizard.relationships.how_to_use.title') || '💡 Cómo usar:'}
          </p>
          <ol className="text-indigo-800 text-sm mt-2 ml-4 list-decimal space-y-1">
            <li>{t('wizard.relationships.how_to_use.step_1')}</li>
            <li>{t('wizard.relationships.how_to_use.step_2')}</li>
            <li>{t('wizard.relationships.how_to_use.step_3')}</li>
            <li>{t('wizard.relationships.how_to_use.step_4')}</li>
          </ol>
          <p className="text-indigo-700 text-xs mt-2 italic">
            {t('wizard.relationships.how_to_use.example')}
          </p>
        </div>
      )} */}

      {tables.length > 1 && (
        <details className="mb-6 cursor-pointer">
          <summary className="p-4 bg-indigo-50 border-l-4 border-indigo-600 rounded-r-lg text-indigo-900 text-sm font-semibold">
            {t('wizard.relationships.how_to_use.title')}
          </summary>
          <div className="mt-2 p-4 bg-indigo-50 border-l-4 border-indigo-600 rounded-r-lg">
            <ol className="list-decimal list-inside space-y-1 text-indigo-900 text-sm">
              <li>{t('wizard.relationships.how_to_use.step_1')}</li>
              <li>{t('wizard.relationships.how_to_use.step_2')}</li>
              <li>{t('wizard.relationships.how_to_use.step_3')}</li>
              <li>{t('wizard.relationships.how_to_use.step_4')}</li>
            </ol>
            <p className="mt-2 text-indigo-900 text-sm">{t('wizard.relationships.how_to_use.example')}</p>
          </div>
        </details>
      )}

      {mode === 'target' && (
        <div className="mb-5">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('wizard.relationships.relation_type') || 'Tipo de relación entre tablas destino'}
          </label>
          <div className="grid grid-cols-2 gap-3">

            {/* Opción: Sin relación */}
            <button
              type="button"
              onClick={() => setNewRelation(prev => ({ ...prev, joinType: 'NONE' }))}
              className={`p-3 rounded-xl border-2 text-left transition-all ${newRelation.joinType === 'NONE'
                ? 'border-slate-600 bg-slate-50 text-slate-800'
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                }`}
            >
              <span className="font-bold text-sm block mb-0.5">
                📋 {t('wizard.relationships.no_relation') || 'Sin relación'}
              </span>
              <span className="text-[11px] leading-relaxed">
                {t('wizard.relationships.no_relation_desc') ||
                  'Tablas independientes. Se escriben en paralelo.'}
              </span>
            </button>

            {/* Opción: FK Padre→Hijo */}
            <button
              type="button"
              onClick={() => setNewRelation(prev => ({ ...prev, joinType: 'FK' }))}
              className={`p-3 rounded-xl border-2 text-left transition-all ${newRelation.joinType === 'FK'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-800'
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                }`}
            >
              <span className="font-bold text-sm block mb-0.5">
                🔗 {t('wizard.relationships.fk_relation') || 'FK Padre → Hijo'}
              </span>
              <span className="text-[11px] leading-relaxed">
                {t('wizard.relationships.fk_relation_desc') ||
                  'Una tabla referencia a otra. DatiaFlow respeta el orden de escritura.'}
              </span>
            </button>

          </div>
        </div>
      )}



      {/* Solo mostrar el formulario de tablas/columnas si es FK o source */}
      {(mode === 'source' || newRelation.joinType === 'FK') && (
        <div>
          {/* Formulario para agregar relación */}
          {tables.length > 1 && (
            <div className="mb-8 p-6 bg-slate-50 border border-slate-200 rounded-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                ➕ {t('wizard.relationships.add_relation') || 'Agregar Relación'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Tabla origen */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {mode === 'target'
                      ? (t('wizard.relationships.target_parent_table') || 'Tabla Padre')
                      : (t('wizard.table_from') || 'Tabla Principal (Origen del JOIN)')
                    }
                  </label>
                  <select
                    value={newRelation.tableFrom}
                    onChange={(e) =>
                      setNewRelation({
                        ...newRelation,
                        tableFrom: e.target.value,
                        columnFrom: '',
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">{t('wizard.relationships.select_option') || '-- Selecciona --'}</option>
                    {tables.map((table) => (
                      <option key={table} value={table}>
                        {table}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Columna origen + reglas */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {mode === 'target'
                      ? (t('wizard.relationships.target_pk_col') || 'Columna PK del Padre')
                      : (t('wizard.column_from') || 'Columna Principal')
                    }
                  </label>
                  <select
                    value={newRelation.columnFrom}
                    onChange={(e) =>
                      setNewRelation({ ...newRelation, columnFrom: e.target.value })
                    }
                    disabled={!newRelation.tableFrom}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-indigo-500 disabled:opacity-50"
                  >
                    <option value="">{t('wizard.relationships.select_option') || '-- Selecciona --'}</option>
                    {cachedColumns[newRelation.tableFrom]?.map((col) => (
                      <option key={col.name} value={col.name}>
                        {col.name}
                        {col.is_pk ? ' [PK]' : ''}
                        {col.is_fk ? ' [FK]' : ''}
                        {col.type ? ` (${col.type}${col.length ? `/${col.length}` : ''})` : ''}
                      </option>
                    ))}
                  </select>
                  {/* 🆕 Reglas sobre la clave primaria */}
                  <JoinRulePicker
                    rules={newRelation.rulesFrom}
                    onChange={(r) => setNewRelation({ ...newRelation, rulesFrom: r })}
                    colName={newRelation.columnFrom}
                    disabled={!newRelation.columnFrom}
                  />
                </div>

                {/* Tabla destino */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {mode === 'target'
                      ? (t('wizard.relationships.target_child_table') || 'Tabla Hijo')
                      : (t('wizard.table_to') || 'Tabla Secundaria (Dependiente)')
                    }
                  </label>
                  <select
                    value={newRelation.tableTo}
                    onChange={(e) =>
                      setNewRelation({
                        ...newRelation,
                        tableTo: e.target.value,
                        columnTo: '',
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">{t('wizard.relationships.select_option') || '-- Selecciona --'}</option>
                    {tables.map((table) => (
                      <option key={table} value={table}>
                        {table}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Columna destino + reglas */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {mode === 'target'
                      ? (t('wizard.relationships.target_fk_col') || 'Columna FK del Hijo')
                      : (t('wizard.column_to') || 'Columna Destino (Secundaria)')
                    }
                  </label>
                  <select
                    value={newRelation.columnTo}
                    onChange={(e) =>
                      setNewRelation({ ...newRelation, columnTo: e.target.value })
                    }
                    disabled={!newRelation.tableTo}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-indigo-500 disabled:opacity-50"
                  >
                    <option value="">{t('wizard.relationships.select_option') || '-- Selecciona --'}</option>
                    {getColumnsForTable(newRelation.tableTo).map((col) => (
                      <option key={col.name} value={col.name}>
                        {col.name}
                        {col.is_pk ? ' [PK]' : ''}
                        {col.is_fk ? ' [FK]' : ''}
                        {col.type ? ` (${col.type}${col.length ? `/${col.length}` : ''})` : ''}
                      </option>
                    ))}
                  </select>
                  {/* 🆕 Reglas sobre la clave secundaria */}
                  <JoinRulePicker
                    rules={newRelation.rulesTo}
                    onChange={(r) => setNewRelation({ ...newRelation, rulesTo: r })}
                    colName={newRelation.columnTo}
                    disabled={!newRelation.columnTo}
                  />
                </div>

                {/* Tipo de JOIN (source) / Indicador FK (target) */}
                <div className="md:col-span-2">
                  {mode === 'target' ? (
                    // 🔧 Target: no es un JOIN — es una relación FK padre→hijo
                    <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <span className="text-xs font-bold text-indigo-700 bg-indigo-100 px-2 py-1 rounded uppercase tracking-wider">
                        FK
                      </span>
                      <p className="text-xs text-indigo-600 leading-relaxed">
                        {t('wizard.relationships.fk_desc') ||
                          'El valor del FK del hijo proviene del mismo campo origen mapeado al PK del padre. DatiaFlow escribe el padre primero, garantizando integridad referencial.'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('wizard.join_type') || 'Tipo de JOIN'}
                      </label>
                      <select
                        value={newRelation.joinType}
                        onChange={(e) =>
                          setNewRelation({ ...newRelation, joinType: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-indigo-500"
                      >
                        {supportedJoins.includes('INNER') && (
                          <option value="INNER">{t('wizard.relationships.join_inner') || 'INNER JOIN (solo coincidencias)'}</option>
                        )}
                        {supportedJoins.includes('LEFT') && (
                          <option value="LEFT">{t('wizard.relationships.join_left') || 'LEFT JOIN (todos del origen)'}</option>
                        )}
                        {supportedJoins.includes('RIGHT') && (
                          <option value="RIGHT">{t('wizard.relationships.join_right') || 'RIGHT JOIN (todos del destino)'}</option>
                        )}
                        {supportedJoins.includes('FULL') && (
                          <option value="FULL">{t('wizard.relationships.join_full') || 'FULL OUTER JOIN (todos)'}</option>
                        )}
                      </select>
                    </>
                  )}
                </div>
                
                {/* ── ID Mapping: Anchor + Bridge — solo target FK ── */}
                {mode === 'target' && newRelation.joinType === 'FK' && (
                  <div className="md:col-span-2 mt-2 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-amber-600 text-sm font-bold shrink-0">⚡</span>
                      <p className="text-xs text-amber-700 leading-relaxed">
                        {t('wizard.relationships.id_mapping_desc') ||
                          '¿El destino genera IDs nuevos automáticamente? Configura estos dos campos ' +
                          'para que DatiaFlow conecte automáticamente al hijo con el nuevo ID del padre.'}
                      </p>
                    </div>

                    {/* Anchor Source — columna del ORIGEN que identifica al padre */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('wizard.relationships.anchor_source') || 'ID original del padre (Origen)'}
                        <span className="text-slate-400 font-normal ml-1">
                          ({t('common.optional') || 'opcional'})
                        </span>
                      </label>
                      <select
                        value={newRelation.anchorSource}
                        onChange={(e) => setNewRelation({ ...newRelation, anchorSource: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-amber-500 text-sm"
                      >
                        <option value="">
                          {t('wizard.relationships.anchor_source_ph') ||
                            '-- Sin resolución automática de IDs --'}
                        </option>
                        {/* Columnas agrupadas por tabla origen */}
                        {Object.entries(cachedSourceCols).map(([tbl, cols]) => (
                          <optgroup key={tbl} label={tbl}>
                            {cols.map(col => (
                              <option key={`${tbl}.${col.name}`} value={`${tbl}.${col.name}`}>
                                {col.name}{col.is_pk ? ' 🔑' : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {t('wizard.relationships.anchor_source_help') ||
                          'Columna del origen que identifica al padre (normalmente su PK). ' +
                          'DatiaFlow la usará para encontrar el nuevo ID generado en destino.'}
                      </p>
                    </div>

                    {/* Bridge Column — columna en la tabla PADRE DESTINO */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('wizard.relationships.bridge_column') || 'Dónde guardar el ID original en destino'}
                        <span className="text-slate-400 font-normal ml-1">
                          ({t('common.optional') || 'opcional'})
                        </span>
                      </label>
                      <select
                        value={newRelation.bridgeColumn}
                        onChange={(e) => setNewRelation({ ...newRelation, bridgeColumn: e.target.value })}
                        disabled={!newRelation.anchorSource || !newRelation.tableFrom}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-amber-500 text-sm disabled:opacity-50"
                      >
                        <option value="">
                          {t('wizard.relationships.bridge_column_ph') || '-- Selecciona columna puente --'}
                        </option>
                        {cachedColumns[newRelation.tableFrom]?.map(col => (
                          <option key={col.name} value={col.name}>
                            {col.name}
                            {col.is_pk ? ' [PK]' : ''}
                            {col.type ? ` (${col.type})` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {t('wizard.relationships.bridge_column_help') ||
                          'Columna en la tabla padre destino donde se guardará el valor anchor ' +
                          'para hacer el lookup post-insert y resolver el nuevo ID.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Error de validación */}
              {validationError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3 shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{validationError}</p>
                </div>
              )}

              {/* Botón agregar */}
              {/* <button
                onClick={addRelationship}
                className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('wizard.relationships.add_relation') || 'Agregar Relación'}
              </button> */}
              {/* 🔧 Ocultar botón en NONE mode — no hay nada que añadir */}
              {(mode === 'source' || newRelation.joinType === 'FK') && (
                <button
                  type="button"
                  onClick={addRelationship}
                  className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('wizard.relationships.add_relation') || '+ Añadir relación'}
                </button>
              )}
              {/* 🆕 AQUÍ VA EL TIPS DE TIPOS DE JOIN */}
              {mode === 'source' && (
                <details className="cursor-pointer mt-4">
                  <summary>{t('wizard.relationships.join_types.title')}</summary>
                  <ul>
                    {supportedJoins.includes('INNER') && <li><strong>INNER:</strong> {t('wizard.relationships.join_types.inner')}</li>}
                    {supportedJoins.includes('LEFT') && <li><strong>LEFT:</strong> {t('wizard.relationships.join_types.left')}</li>}
                    {supportedJoins.includes('RIGHT') && <li><strong>RIGHT:</strong> {t('wizard.relationships.join_types.right')}</li>}
                    {supportedJoins.includes('FULL') && <li><strong>FULL:</strong> {t('wizard.relationships.join_types.full')}</li>}
                  </ul>
                </details>
              )}

            </div>
          )}
        </div>
      )}
      {/* Lista de relaciones definidas */}
      {/* ── Modal advertencia cardinalidad 1:N ─────────────────── */}
      {cardinalityWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-bold text-slate-900 text-base mb-1">
                  {t('wizard.relationships.cardinality_warning_title') ||
                    'Posible duplicación de filas en destino'}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {(t('wizard.relationships.cardinality_warning_body') ||
                    'La columna "{col}" en "{table}" puede tener múltiples filas por cada fila origen. Esto duplicaría los registros de "{parent}" al destino.')
                    .replace('{col}',    cardinalityWarning.col)
                    .replace('{table}',  cardinalityWarning.table)
                    .replace('{parent}', cardinalityWarning.parent)
                  }
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
              <p className="text-xs text-amber-800 leading-relaxed">
                💡 {t('wizard.relationships.cardinality_warning_tip') ||
                  'Si necesitas migrar ambas tablas conservando los nuevos IDs, usa dos pipelines separados con un campo puente (_old) y un UPDATE SQL post-migración.'}
              </p>
            </div>

            <p className="text-sm font-medium text-slate-700 mb-4">
              {t('wizard.relationships.cardinality_warning_cta') ||
                '¿Deseas continuar de todas formas?'}
            </p>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setCardinalityWarning(null);
                  setPendingRelation(null);
                }}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium"
              >
                {t('common.buttons.cancel') || 'Cancelar'}
              </button>
              <button
                type="button"
                onClick={() => _commitRelation(pendingRelation)}
                className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold"
              >
                {t('common.buttons.continue_anyway') || 'Continuar de todas formas'}
              </button>
            </div>
          </div>
        </div>
      )}
      {relationshipsLocal.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mr-2" />
            {t('wizard.relationships.defined') || 'Relaciones Definidas'} (
            {relationshipsLocal.length})
          </h3>

          <div className="space-y-3">
            {relationshipsLocal.map((rel, idx) => (
              <div key={idx} className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">

                {/* Fila principal: tabla + columnas + tipo + botón delete */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-wrap gap-1 flex-1">

                    {/* Lado primario */}
                    <span className="font-medium text-slate-900">{rel.tableFrom}</span>
                    <span className="text-slate-500 text-sm">({rel.columnFrom})</span>
                    {(rel.rulesFrom || []).map(r => (
                      <span key={r.id} className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-bold rounded-md">
                        {r.id}
                      </span>
                    ))}

                    <ArrowRight className="w-4 h-4 text-slate-400 mx-1" />

                    {/* Lado secundario */}
                    <span className="font-medium text-slate-900">{rel.tableTo}</span>
                    <span className="text-slate-500 text-sm">({rel.columnTo})</span>
                    {(rel.rulesTo || []).map(r => (
                      <span key={r.id} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded-md">
                        {r.id}
                      </span>
                    ))}

                    <span className="ml-auto px-2 py-1 bg-emerald-200 text-emerald-800 text-xs font-bold rounded flex-shrink-0">
                      {rel.joinType}
                    </span>
                  </div>

                  {/* Botón eliminar */}
                  <button
                    onClick={() => removeRelationship(idx)}
                    className="ml-4 text-red-600 hover:text-red-800 p-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>  {/* ← cierra flex items-center justify-between */}

                {/* Anchor + Bridge — solo si están configurados (ID Mapping) */}
                {(rel.anchorSource || rel.bridgeColumn) && (
                  <div className="mt-2 pt-2 border-t border-emerald-200 flex flex-wrap gap-3 text-[11px]">
                    {rel.anchorSource && (
                      <span className="flex items-center gap-1 text-amber-700">
                        <span className="font-bold">🔑 ID origen:</span>
                        <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">
                          {rel.anchorSource}
                        </span>
                      </span>
                    )}
                    {rel.bridgeColumn && (
                      <span className="flex items-center gap-1 text-amber-700">
                        <span className="font-bold">🌉 Campo puente:</span>
                        <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">
                          {rel.bridgeColumn}
                        </span>
                      </span>
                    )}
                  </div>
                )}

              </div>  
            ))}
          </div>
        </div>
      )}

      {/* Warning solo aplica en source o cuando es FK — en NONE es válido no tener relaciones */}
      {tables.length > 1 && relationshipsLocal.length === 0 &&
        (mode === 'source' || newRelation.joinType === 'FK') && (
          <div className="mb-8 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
            <p className="text-amber-800 font-semibold text-sm">
              ⚠️{' '}
              {t('wizard.relationships.error_no_relationships') ||
                'Debes definir al menos una relación entre las tablas'}
            </p>
          </div>
        )}

      {/* Botones nav */}
      <div className="flex justify-between border-t border-slate-200 pt-6">
        <button
          onClick={prevStep}
          className="px-6 py-2 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
        >
          ← {t('wizard.back') || 'Atrás'}
        </button>

        <button
          onClick={handleContinue}
          // disabled={tables.length > 1 && relationshipsLocal.length === 0}
          disabled={tables.length > 1 && !allTablesHaveRelation}
          className="px-6 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
        >
          {t('wizard.continue') || 'Continuar'} →
        </button>
      </div>
      {/* 🆕 TIPS DE JOINS */}
      {/* <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
        <details className="cursor-pointer">
          <summary>{t('wizard.relationships.join_types.title')}</summary>
          <ul>
            {supportedJoins.includes('INNER') && <li><strong>INNER:</strong> {t('wizard.relationships.join_types.inner')}</li>}
            {supportedJoins.includes('LEFT') && <li><strong>LEFT:</strong> {t('wizard.relationships.join_types.left')}</li>}
            {supportedJoins.includes('RIGHT') && <li><strong>RIGHT:</strong> {t('wizard.relationships.join_types.right')}</li>}
            {supportedJoins.includes('FULL') && <li><strong>FULL:</strong> {t('wizard.relationships.join_types.full')}</li>}
          </ul>
        </details>
      </div> */}
    </div>
  );
}
